import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@ibsys/kit';
import type { ComboboxItem } from '@ibsys/kit';
import { useConversationsAttributes } from '@/entities/conversations';
import type { StrategistTargetAttribute } from '@/entities/strategist';

/** Значение первого комбобокса, соответствующее целевой метрике «Удержание». */
const RETENTION_OPTION = '__retention__';

/** Значение первого комбобокса, соответствующее отсутствию целевой метрики. */
const NONE_OPTION = '__none__';

/**
 * Пропсы {@link TargetMetricPicker}. Вариант «Нет» (метрика не выбрана) включается
 * пропом `isNoneAllowed` и только вместе с ним допускает `undefined` в `value`/`onChange`:
 * страницы, где метрика обязательна, продолжают работать с непустым значением.
 */
type Props =
  | {
      /** Вариант «Нет» выключен: метрика обязательна */
      isNoneAllowed?: false;
      /** Текущая целевая метрика */
      value: StrategistTargetAttribute;
      /** Колбэк смены целевой метрики */
      onChange: (next: StrategistTargetAttribute) => void;
    }
  | {
      /** Вариант «Нет» включён: метрику можно не выбирать */
      isNoneAllowed: true;
      /** Текущая целевая метрика; `undefined` — не выбрана */
      value: StrategistTargetAttribute | undefined;
      /** Колбэк смены целевой метрики; `undefined` — метрика снята */
      onChange: (next: StrategistTargetAttribute | undefined) => void;
    };

/**
 * Компактный выбор целевой метрики раздела «Стратег»: «Удержание» по умолчанию
 * либо произвольный атрибут + его значение. При `isNoneAllowed` первым вариантом
 * идёт «Нет» — метрика не выбрана, наверх уходит `undefined`. Список атрибутов
 * берётся тем же хуком, что и сквозные фильтры (`useConversationsAttributes`);
 * для значений используются `selectValues` выбранного атрибута.
 */
export const TargetMetricPicker: FC<Props> = (props) => {
  const { value, isNoneAllowed = false } = props;
  const { t } = useTranslation('strategist');
  const { data } = useConversationsAttributes({ limit: 1000 });

  // Локально выбранный путь атрибута: «нет», retention или конкретный path.
  const [selectedPath, setSelectedPath] = useState<string>(
    () => value?.attributePath ?? (value ? RETENTION_OPTION : NONE_OPTION),
  );

  // Только перечислимые атрибуты (с готовым списком значений) — их можно задать как цель.
  const enumerableAttributes = useMemo(
    () =>
      (data?.attributes ?? []).filter(
        (a) => !a.isDeleted && Array.isArray(a.selectValues) && a.selectValues.length > 0,
      ),
    [data?.attributes],
  );

  const metricOptions = useMemo<ComboboxItem[]>(
    () => [
      ...(isNoneAllowed ? [{ value: NONE_OPTION, label: t('target.none') }] : []),
      { value: RETENTION_OPTION, label: t('target.retention') },
      ...enumerableAttributes.map((a) => ({ value: a.path, label: a.pathDescription || a.name })),
    ],
    [enumerableAttributes, isNoneAllowed, t],
  );

  const selectedAttribute = useMemo(
    () => enumerableAttributes.find((a) => a.path === selectedPath),
    [enumerableAttributes, selectedPath],
  );

  // В метрику уходит `code` значения, а не его лейбл: бэк сравнивает целевое
  // значение с колонкой `value_select_code` (там коды, не подписи) — так же,
  // как это делает фильтр атрибутов
  const valueOptions = useMemo<ComboboxItem[]>(
    () =>
      (selectedAttribute?.selectValues ?? [])
        .filter((v) => !v.isDeleted)
        .map((v) => ({ value: v.code, label: v.value })),
    [selectedAttribute],
  );

  // Единая точка отдачи метрики наверх: снять метрику можно только там, где
  // вариант «Нет» вообще есть — иначе `undefined` неоткуда взяться.
  const emitChange = (next: StrategistTargetAttribute | undefined) => {
    if (props.isNoneAllowed) {
      props.onChange(next);
      return;
    }
    // Вариант «Нет» выключен — пустой метрике взяться неоткуда.
    if (next !== undefined) props.onChange(next);
  };

  const handleMetricChange = (next?: string | number) => {
    const fallback = isNoneAllowed ? NONE_OPTION : RETENTION_OPTION;
    const path = String(next ?? fallback);
    setSelectedPath(path);
    if (path === NONE_OPTION) {
      emitChange(undefined);
      return;
    }
    if (path === RETENTION_OPTION) {
      emitChange({ retention: true });
      return;
    }
    // Атрибут выбран, значение ещё нет — метрика неполна: там, где «Нет» разрешено,
    // она остаётся невыбранной, иначе (метрика обязательна) остаёмся на удержании.
    emitChange(isNoneAllowed ? undefined : { retention: true });
  };

  const handleValueChange = (next?: string | number) => {
    if (!selectedAttribute || next === undefined || next === null) return;
    emitChange({ attributePath: selectedAttribute.path, value: String(next) });
  };

  // Второй комбобокс нужен, только когда целью выбран конкретный атрибут.
  const isAttributeSelected = selectedPath !== RETENTION_OPTION && selectedPath !== NONE_OPTION;

  return (
    <div className="flex flex-1 flex-wrap items-end gap-4">
      <Combobox
        label={t('target.label')}
        options={metricOptions}
        value={selectedPath}
        onChange={handleMetricChange}
        className="min-w-60 flex-1"
      />
      {isAttributeSelected && (
        <Combobox
          label={t('target.value')}
          options={valueOptions}
          value={value?.attributePath === selectedAttribute?.path ? value?.value : undefined}
          onChange={handleValueChange}
          placeholder={t('target.valuePlaceholder')}
          emptyMessage={t('target.noValues')}
          className="min-w-52 flex-1"
        />
      )}
    </div>
  );
};
